/* eslint-disable react/prop-types */
import { CircularProgress, TableCell, TableRow, Chip, IconButton, Collapse, Checkbox, Box } from '@mui/material'
import React from 'react'
import { getSecondaryWorkItems } from '../../../services/workItemService'
import { WorkItem } from '../../../types/Work/WorkItem'
import AnimatedNumberField from '../../core/AnimatedNumberField'
import { formatLocaleShortDate, formatPercent } from '../../../services/CommonServices/DataFormatters'
import { OpenAttachmentsButton } from '../../attachments/OpenAttachmentsButton'
import AssignedToDialog from '../../core/AssignedToDialog'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { showErrorToast } from '../../../services/CommonServices/alertServices'

interface WorkItemRowProps {
	workItem: WorkItem
	layer: number
	selectedWorkItems: {id: number, path: string}[]
	adjacentSelectedIds: number[]
	addSelectedWorkItem: (workItemId: number) => void
	removeSelectedWorkItem: (workItemId: number) => void
	onChildrenLoaded: (parentId: number, children: WorkItem[] | undefined) => void
	getChildrenForParent: (parentId: number) => WorkItem[] | undefined
}

export function WorkItemRow({
	workItem,
	layer,
	selectedWorkItems,
	adjacentSelectedIds,
	addSelectedWorkItem,
	removeSelectedWorkItem,
	onChildrenLoaded,
	getChildrenForParent,
}: WorkItemRowProps) {
	const [open, setOpen] = React.useState(false)
	const [loadingChildren, setLoadingChildren] = React.useState(false)

	const childrenWorkItems = getChildrenForParent(workItem.id) ?? workItem.childrenWorkItems

	const isSelected =
		selectedWorkItems.some((w) => w.id === workItem.id) || adjacentSelectedIds.includes(workItem.id)
	const isAdjacentSelected = adjacentSelectedIds.includes(workItem.id)

	const getLayerVariant = (): any => {
		return `Layer${workItem.wbsIndex}${workItem.outOfScope ? 'OutOfScope' : 'WorkItem'}` as any
	}

	const outOfBoundsAssignedTo = (assignedTo: string | null | undefined): boolean => {
		return (assignedTo?.split(',').length ?? 0) > 2
	}

	React.useEffect(() => {
		if (!open) return
		// If children are already loaded in the parent, use them
		if (getChildrenForParent(workItem.id) !== undefined) {
			return
		}
		// If workItem has children, register them
		if (workItem.childrenWorkItems && workItem.childrenWorkItems.length > 0) {
			onChildrenLoaded(workItem.id, workItem.childrenWorkItems)
			return
		}
		// Otherwise, fetch children
		setLoadingChildren(true)
		getSecondaryWorkItems(workItem.id)
			.then((res: WorkItem) => {
				if (res.id !== workItem.id) {
					showErrorToast({ message: 'Wrong work item fetched.' })
					return
				}
				onChildrenLoaded(workItem.id, res.childrenWorkItems ?? [])
			})
			.finally(() => {
				setLoadingChildren(false)
			})
	}, [open, workItem.id, workItem.childrenWorkItems, getChildrenForParent, onChildrenLoaded])

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.stopPropagation()
		if (selectedWorkItems.some((w) => w.id === workItem.id)) {
			removeSelectedWorkItem(workItem.id)
		} else {
			addSelectedWorkItem(workItem.id)
		}
	}

	return (
		<>
			<TableRow
				key={workItem.id}
				sx={{
					cursor: 'pointer',
					backgroundColor: isSelected ? 'action.selected' : 'inherit',
					'&:hover': {
						backgroundColor: 'action.hover',
					},
				}}
			>
				<TableCell>
					<Box
						sx={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: '0.5rem',
						}}
					>
						<Checkbox
							checked={isSelected}
							disabled={isAdjacentSelected}
							onChange={handleCheckboxChange}
							sx={{
								'& .MuiSvgIcon-root': {
									transition: 'transform 0.2s ease-in-out',
								},
								'&:hover .MuiSvgIcon-root': {
									transform: 'scale(1.1)',
								},
								p: 0,
							}}
						/>
						{workItem.tasks.length === 0 && (workItem.childrenWorkItems || layer === 1) && (
							<IconButton
								sx={{ pl: layer - 1 }}
								aria-label='expand row'
								size='small'
								onClick={(e) => {
									e.stopPropagation()
									setOpen(!open)
								}}
							>
								{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
							</IconButton>
						)}
					</Box>
				</TableCell>
				<TableCell align='center'>
					<Chip label={workItem.typeName} variant={getLayerVariant()} />
				</TableCell>
				<TableCell align='center'>
					<div>{workItem.outOfScope ? workItem.wbsName : workItem.craftName}</div>
				</TableCell>
				<TableCell align='center'></TableCell>
				<TableCell align='left'>{workItem.name}</TableCell>
				<TableCell align='center'>
					<OpenAttachmentsButton
						selectAttachments={() => { console.log('selectAttachments', workItem.id) }}
						attachmentCount={workItem.attachmentCount}
					/>
				</TableCell>
				<TableCell align='center'>{formatLocaleShortDate(workItem.releaseDate)}</TableCell>
				<TableCell align='center'>{formatLocaleShortDate(workItem.nocDate)}</TableCell>
				<TableCell align='right'>
					<AnimatedNumberField value={workItem.quantityBudgeted} />
				</TableCell>
				<TableCell align='right'></TableCell>
				<TableCell align='right'>{workItem.unitOfMeasure}</TableCell>
				<TableCell align='right'>
					<AnimatedNumberField value={workItem.quantityEarned} />
				</TableCell>
				<TableCell align='right'>
					<AnimatedNumberField value={workItem.quantityRemaining} />
				</TableCell>
				<TableCell align='right'>
					<AnimatedNumberField value={formatPercent(workItem.percentComplete)} />
				</TableCell>
				<TableCell align='center'></TableCell>
				<TableCell align='center'>
					<AnimatedNumberField value={Math.trunc(workItem.budgetedHours)} />
				</TableCell>
				<TableCell align='center'>
					<AnimatedNumberField value={workItem.earnedHours} />
				</TableCell>
				<TableCell align='center'>
					<AnimatedNumberField value={workItem.burnedHours} />
				</TableCell>
				<TableCell align='right'>
					<AnimatedNumberField value={workItem.pf} />
				</TableCell>
				<TableCell align='right'>
					{outOfBoundsAssignedTo(workItem.assignedTo) && workItem.assignedTo ? (
						<AssignedToDialog assignedTo={workItem.assignedTo} />
					) : (
						workItem.assignedTo
					)}
				</TableCell>
			</TableRow>

			<Collapse in={loadingChildren} timeout='auto'>
				<TableRow>
					<TableCell colSpan={20} align='center'>
						<CircularProgress color='success' />
					</TableCell>
				</TableRow>
			</Collapse>

			{open &&
				childrenWorkItems &&
				childrenWorkItems.map((child, index) => (
					<WorkItemRow
						key={child.id || index}
						workItem={child}
						layer={layer + 1}
						selectedWorkItems={selectedWorkItems}
						adjacentSelectedIds={adjacentSelectedIds}
						addSelectedWorkItem={addSelectedWorkItem}
						removeSelectedWorkItem={removeSelectedWorkItem}
						onChildrenLoaded={onChildrenLoaded}
						getChildrenForParent={getChildrenForParent}
					/>
				))}
		</>
	)
}
